import { Button, Popconfirm, Row } from 'antd';
import React, { useCallback, useState } from 'react';
import { Modal } from '../../../../components/Modal/Modal';
import { TextAreaWithCharacterCount } from '../../../../components/TextAreaWithCharacterCount';
import { useTypedSelector } from '../../../../core-module/redux';
import { absenceClient } from '../../../../core-module/services/absence/AbsenceClient';

interface Props {
  deployabilityDayId: number;
  driverDeployabilityDayNoteId?: number;
  note?: string;
  refresh: () => void;
}

const DayNote: React.FC<Props> = ({
  deployabilityDayId,
  driverDeployabilityDayNoteId,
  note,
  refresh
}) => {
  const driverId = useTypedSelector((state) => state.auth.user.driver!.id);
  const [modalOpened, setModalOpened] = useState(false);
  const [okButtonLoading, setOkButtonLoading] = useState(false);
  const [textValue, setTextValue] = useState(note);

  const trimmedValue = textValue?.trim();

  const createDriverDeployabilityDayNote = useCallback(
    async (deployabilityDayId: number, textValue: string) => {
      await absenceClient.createDriverDeployabilityDayNote(driverId, deployabilityDayId, textValue);
    },
    [driverId]
  );

  const updateDriverDeployabilityDayNote = useCallback(
    async (driverDeployabilityDayNoteId: number, textValue: string) => {
      await absenceClient.updateDriverDeployabilityDayNote(
        driverId,
        driverDeployabilityDayNoteId,
        textValue
      );
    },
    [driverId]
  );

  const deleteDriverDeployabilityDayNote = useCallback(
    async (driverDeployabilityDayNoteId: number) => {
      await absenceClient.removeDriverDeployabilityDayNote(driverId, driverDeployabilityDayNoteId);
    },
    [driverId]
  );

  const onSave = useCallback(async () => {
    setOkButtonLoading(true);
    if (!trimmedValue && driverDeployabilityDayNoteId !== undefined) {
      await deleteDriverDeployabilityDayNote(driverDeployabilityDayNoteId);
      refresh();
      setModalOpened(false);
    } else if (trimmedValue && driverDeployabilityDayNoteId !== undefined) {
      await updateDriverDeployabilityDayNote(driverDeployabilityDayNoteId, trimmedValue);
      refresh();
      setModalOpened(false);
    } else if (trimmedValue) {
      await createDriverDeployabilityDayNote(deployabilityDayId, trimmedValue);
      refresh();
      setModalOpened(false);
    }

    setOkButtonLoading(false);
  }, [
    deployabilityDayId,
    driverDeployabilityDayNoteId,
    createDriverDeployabilityDayNote,
    deleteDriverDeployabilityDayNote,
    updateDriverDeployabilityDayNote,
    refresh,
    trimmedValue
  ]);

  return (
    <>
      <Row type='flex' justify='space-between'>
        {note}
        <div>
          <Button
            type='primary'
            icon='edit'
            onClick={() => setModalOpened(true)}
            style={{ marginRight: 4 }}
          />
          {driverDeployabilityDayNoteId && (
            <Popconfirm
              title='Weet u het zeker?'
              onConfirm={async () => {
                await deleteDriverDeployabilityDayNote(driverDeployabilityDayNoteId);
                refresh();
              }}
            >
              <Button type='danger' icon='delete' />
            </Popconfirm>
          )}
        </div>
      </Row>
      <Modal
        visible={modalOpened}
        title='Vul hier de opmerking in'
        cancelText='Annuleren'
        okText='Opslaan'
        onCancel={() => setModalOpened(false)}
        onOk={onSave}
        okButtonDisabled={trimmedValue === note}
        okButtonLoading={okButtonLoading}
      >
        <TextAreaWithCharacterCount
          showCharacterCount={true}
          maxLength={255}
          value={textValue}
          placeholder='Vul hier je opmerking in..'
          onChange={(value) => {
            setTextValue(value);
          }}
        />
      </Modal>
    </>
  );
};

export { DayNote };
